<template>
  <div class="chart-container">
    <div class="chart-header">
      <div class="legend-info">
        <span class="legend-item critical">Critico</span>
        <span class="legend-item warning">Atencao</span>
        <span class="legend-item ok">OK</span>
      </div>
    </div>
    <div ref="targetChart" style="height: 450px; width: 100%; margin-top: 10px;"></div>
  </div>
</template>

<script>
import Highcharts from 'highcharts'
import HighchartsMore from 'highcharts/highcharts-more'
import ChartMix from '@/components/charts/ChartMix.vue'
import ChartThemeMixin from '@/components/charts/ChartThemeMixin.vue'
import Formatter from '@/models/Formatter'

HighchartsMore(Highcharts)

export default {
  props: {
    graphData: { required: true, type: Array },
    graphTitle: { required: true, type: String },
    loading: { required: true, type: Boolean },
  },
  data() {
    return {
      riskColors: {
        critical: '#FF6B6B',
        warning: '#FFB347',
        ok: '#4ECDC4'
      }
    }
  },
  computed: {
    processedData() {
      if (!this.graphData || this.graphData.length === 0) {
        return { data: [], avgCount: 0, avgValue: 0 }
      }

      const validData = this.graphData.filter(item => item.count > 0 && item.sum > 0)

      if (validData.length === 0) {
        return { data: [], avgCount: 0, avgValue: 0 }
      }

      const avgCount = validData.reduce((acc, item) => acc + item.count, 0) / validData.length
      const avgValue = validData.reduce((acc, item) => acc + (item.sum / 100), 0) / validData.length

      const data = validData.map(item => {
        const valueInReais = item.sum / 100
        const riskLevel = this.calculateRiskLevel(item.count, valueInReais, avgCount, avgValue)

        return {
          x: item.count,
          y: valueInReais,
          z: Math.max(item.count, 5),
          name: item.name || this.formatCNPJ(item.cnpj),
          cnpj: item.cnpj,
          riskLevel: riskLevel,
          color: this.riskColors[riskLevel]
        }
      })

      return { data, avgCount, avgValue }
    }
  },
  methods: {
    formatCNPJ(cnpj) {
      if (!cnpj) return 'N/A'
      const cleaned = cnpj.replace(/[^0-9A-Za-z]/g, '')
      if (cleaned.length !== 14) return cnpj
      return Formatter.cnpj(cleaned)
    },
    calculateRiskLevel(count, value, avgCount, avgValue) {
      const isHighCount = count >= avgCount
      const isHighValue = value >= avgValue

      if (isHighCount && isHighValue) {
        return 'critical'
      } else if (isHighCount || isHighValue) {
        return 'warning'
      } else {
        return 'ok'
      }
    },
    getRiskLabel(riskLevel) {
      const labels = {
        critical: 'CRITICO',
        warning: 'ATENCAO',
        ok: 'OK'
      }
      return labels[riskLevel] || 'N/A'
    },
    createChart() {
      if (this.$refs.targetChart) {
        const existingChart = Highcharts.charts.find(chart =>
          chart && chart.renderTo === this.$refs.targetChart
        )
        if (existingChart) {
          existingChart.destroy()
        }
        this.$refs.targetChart.innerHTML = ''
      }

      const theme = this.chartTheme
      const { data, avgCount, avgValue } = this.processedData
      const self = this

      const chartConfig = {
        chart: {
          type: 'bubble',
          backgroundColor: theme.backgroundColor,
          plotBorderWidth: 1,
          plotBorderColor: theme.gridLineColor,
          zoomType: 'xy',
          height: 450,
          marginLeft: 85,
          marginBottom: 70,
          style: { fontFamily: '"Montserrat", sans-serif' }
        },
        credits: {
          enabled: false
        },
        title: {
          text: this.currentTitle,
          align: 'center',
          style: {
            color: theme.textColor,
            fontSize: '16px',
            fontWeight: '600'
          }
        },
        subtitle: {
          text: data.length > 0 ? 'Canto superior direito = maior risco' : '',
          style: {
            color: theme.textColor,
            fontSize: '11px'
          }
        },
        xAxis: {
          title: {
            text: 'Quantidade de Multas',
            style: {
              color: theme.textColor,
              fontSize: '13px',
              fontWeight: '600'
            },
            margin: 15
          },
          labels: {
            style: {
              color: theme.textColor,
              fontSize: '11px'
            }
          },
          gridLineColor: theme.gridLineColor,
          gridLineWidth: 1,
          min: 0,
          plotLines: avgCount > 0 ? [{
            value: avgCount,
            color: '#999999',
            dashStyle: 'Dash',
            width: 2,
            zIndex: 3,
            label: {
              text: 'Media',
              style: {
                color: theme.textColor,
                fontSize: '10px',
                fontWeight: 'bold'
              },
              align: 'right',
              x: -5,
              y: 15
            }
          }] : []
        },
        yAxis: {
          title: {
            text: 'Valor Total (R$)',
            style: {
              color: theme.textColor,
              fontSize: '13px',
              fontWeight: '600'
            },
            margin: 15
          },
          labels: {
            style: {
              color: theme.textColor,
              fontSize: '11px'
            },
            formatter: function() {
              if (this.value >= 1000) {
                return 'R$ ' + Highcharts.numberFormat(this.value / 1000, 0) + 'k'
              }
              return 'R$ ' + Highcharts.numberFormat(this.value, 0, ',', '.')
            }
          },
          gridLineColor: theme.gridLineColor,
          min: 0,
          plotLines: avgValue > 0 ? [{
            value: avgValue,
            color: '#999999',
            dashStyle: 'Dash',
            width: 2,
            zIndex: 3,
            label: {
              text: 'Media',
              style: {
                color: theme.textColor,
                fontSize: '10px',
                fontWeight: 'bold'
              },
              align: 'left',
              x: 5,
              y: -5
            }
          }] : []
        },
        tooltip: {
          useHTML: true,
          backgroundColor: theme.backgroundColor,
          borderColor: theme.gridLineColor,
          style: { color: theme.textColor },
          formatter: function() {
            const riskLabel = self.getRiskLabel(this.point.riskLevel)
            const riskColor = self.riskColors[this.point.riskLevel]
            return `
              <div style="padding: 8px;">
                <strong style="font-size: 14px;">${this.point.name}</strong><br/>
                <span style="color: #888; font-size: 11px;">${self.formatCNPJ(this.point.cnpj)}</span><br/><br/>
                <b>Quantidade:</b> ${this.point.x} multas<br/>
                <b>Valor Total:</b> R$ ${Highcharts.numberFormat(this.point.y, 2, ',', '.')}<br/><br/>
                <span style="background: ${riskColor}; color: white; padding: 3px 8px; border-radius: 4px; font-weight: bold;">
                  ${riskLabel}
                </span>
              </div>
            `
          }
        },
        legend: {
          enabled: false
        },
        plotOptions: {
          bubble: {
            minSize: 15,
            maxSize: 50,
            dataLabels: {
              enabled: true,
              format: '{point.name}',
              style: {
                color: theme.textColor,
                textOutline: 'none',
                fontSize: '9px',
                fontWeight: 'normal'
              },
              allowOverlap: false
            },
            cursor: 'pointer'
          }
        },
        series: [{
          name: 'CNPJs',
          data: data
        }]
      }

      if (data.length === 0) {
        chartConfig.title.text = 'Sem dados para exibir'
        chartConfig.subtitle.text = 'Aplique filtros para visualizar a matriz de risco'
      }

      Highcharts.chart(this.$refs.targetChart, chartConfig)
    }
  },
  mixins: [ChartMix, ChartThemeMixin],
  mounted() {
    this.createChart()
  },
  watch: {
    isDarkTheme: {
      handler() {
        this.$nextTick(() => {
          this.createChart()
        })
      },
      immediate: false
    },
    graphData: {
      handler() {
        this.$nextTick(() => {
          this.createChart()
        })
      },
      deep: true
    }
  },
  name: 'CmEmbarcadorRiskMatrixChart'
}
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: visible;
  background-color: var(--white-bg);
  padding: 15px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
}

.chart-header {
  position: absolute;
  top: 15px;
  right: 20px;
  z-index: 10;
  display: flex;
  gap: 8px;
}

.legend-info {
  display: flex;
  gap: 8px;
  font-size: 11px;
}

.legend-item {
  padding: 4px 10px;
  border-radius: 4px;
  font-weight: 600;
  color: white;
}

.legend-item.critical {
  background-color: #FF6B6B;
}

.legend-item.warning {
  background-color: #FFB347;
}

.legend-item.ok {
  background-color: #4ECDC4;
}
</style>
